import getRemoteFolderItemsRecursive from '@gaia/utils/gaia/getRemoteFolderItemsRecursive';
import {useEffect, useState} from 'react';
import useSWRImmutable from 'swr/immutable';

// Returns a comma-separated list of child folder ids
function getFolderChildIds(folder: Folder | undefined) {
    if (folder?.folders && folder.folders.length > 0) {
        return folder.folders.map((f) => f.id).join();
    }
    if (folder?.children) {
        return folder.children.join();
    }
    return '';
}

interface HighlightFolderChildrenProps {
    folders: Record<string, Folder>;
    sidebar: SidebarState;
}

export default function useHighlightActiveFolder({sidebar, folders}: HighlightFolderChildrenProps) {
    const [activeFolderIds, setActiveFolderIds] = useState<string[] | undefined>();

    const activeFolderId = sidebar.type === 'folderDetails' ? sidebar.id : undefined;
    const activeFolder = activeFolderId ? folders[activeFolderId] : undefined;
    const activeFolderChildIds = getFolderChildIds(activeFolder);

    // Fetch the ids of all recursive children of the current folder.
    const {data: recursiveChildIds, isLoading} = useSWRImmutable(
        activeFolder ? [activeFolder.id, activeFolderChildIds] : [''],
        async ([folderId, folderChildIds]: [string | undefined, string | undefined]) => {
            if (folderId && folderChildIds) {
                // Taking a folder local only returns references to its immediate children, but folders may be deeply nested.
                // We rely on the server to get a recursive list of all grandchild ids.
                const [children] = await getRemoteFolderItemsRecursive(folderId);
                return [folderId, ...children.folders];
            }
        }
    );

    // Update the active folder ids when data finishes validating
    // or when a user navigates into or out of a folder
    useEffect(() => {
        if (!isLoading && recursiveChildIds) {
            setActiveFolderIds(recursiveChildIds);
        } else if (activeFolder?.localId && activeFolder.id) {
            // The folder is local and has no children
            setActiveFolderIds([activeFolder.id]);
        }
    }, [isLoading, recursiveChildIds, activeFolder]);

    // Clear the active folder ids if the sidebar is not relevant to saved items
    useEffect(() => {
        const savedItemsSidebars = [
            'editArea',
            'editFolder',
            'editTrack',
            'editWaypoint',
            'folderDetails',
        ];
        if (!savedItemsSidebars.includes(sidebar.type)) {
            setActiveFolderIds(undefined);
        }
    }, [sidebar.type]);

    return [activeFolderIds];
}
